// utils/cartUtils.ts
import { CartItem, CartItemApi } from "@/packages/types/cart";
import { Product, Variant } from "@/packages/types/products";

export function mergeCarts(
  apiCart: CartItem[],
  localCart: CartItem[]
): CartItem[] {
  const map = new Map<string, CartItem>();

  // Add API items first (DB is source of truth)
  apiCart.forEach((item) => {
    const key = getItemKey(item);
    const availableStock = item.variant?.stock ?? item.stock;
    const quantity = Math.min(item.quantity, availableStock); // cap by stock
    map.set(key, { ...item, quantity });
  });

  // Add local items if they don't exist in DB
  localCart.forEach((item) => {
    const key = getItemKey(item);
    if (!map.has(key)) {
      const availableStock = item.variant?.stock ?? item.stock;
      const quantity = Math.min(item.quantity, availableStock);
      map.set(key, { ...item, quantity });
    }
    // if already in DB, ignore local quantity
  });

  // Apply special discounts after merging
  const merged = Array.from(map.values());

  const finalCart = applySpecialDiscountsToCart(merged);

  return finalCart;
}

function getItemKey(item: CartItem) {
  return `${item.productId}-${item?.variant?.id || ""}`;
}

export function transformApiCartItem(apiItem: CartItemApi): CartItem {
  // Step 1: build the product object in local format

  const product: Product = {
    id: apiItem.product.id,
    stock: apiItem.product.stock,
    title: apiItem.product.title,
    salePrice: Number(apiItem.product.salePrice),
    comparePrice: Number(apiItem.product.comparePrice) ?? null,
    slug: apiItem.product.slug,
    averageRating: apiItem.product.averageRating,
    totalRating: apiItem.product.totalRating ?? null,
    variantStatus: apiItem.product.variantStatus,
    thumbnail: apiItem.product.thumbnail,
    images: apiItem.product.images,
    categories: [],
    categoryId: null,
    producttaxes: apiItem.product.producttaxes,
    specialDiscountStatus: apiItem?.product?.specialDiscountStatus,
    specialDiscount: apiItem.product?.specialDiscount ?? null,
  };

  const variant: Variant | undefined = {
    id: apiItem.variant?.id ?? 0,
    purchasePrice: "",
    salePrice: apiItem.variant?.salePrice ?? "",
    comparePrice: apiItem.variant?.comparePrice ?? "",
    stock: apiItem.variant?.stock ?? 0,
    imageId: apiItem.variant?.imageId ?? 0,
    product_image: {
      id: apiItem.variant?.product_image?.id ?? 0,
      imageUrl: apiItem.variant?.product_image?.imageUrl ?? "",
      thumbnailUrl: apiItem.variant?.product_image?.thumbnailUrl ?? "",
      fileName: "",
    },
    variantAttributes: apiItem.variant?.variantAttributes ?? [],
  };
  // Step 2: build cart item (with variant if exists)
  const cartItem: CartItem = {
    productId: apiItem.product.id,
    variantId: apiItem.variant?.id, // use variant if present
    quantity: apiItem.quantity,
    price: parseFloat(apiItem.variant?.salePrice || apiItem.product.salePrice), // use variant salePrice if available, otherwise product salePrice
    ...product,
    variant: apiItem.variant ? variant : undefined, // only include variant if it exists
  };

  // Step 3: return the variable
  return cartItem;
}

export function cartsAreEqual(
  apiCart: CartItem[],
  mergedCart: CartItem[]
): boolean {
  // Normalize both carts → only keep productId, variantId, quantity
  const normalize = (cart: CartItem[]) =>
    cart
      .map((item) => ({
        productId: item.productId,
        variantId: item.variant?.id ?? null,
        quantity: item.quantity,
      }))
      // sort to avoid order issues
      .sort(
        (a, b) =>
          Number(a.productId) - Number(b.productId) ||
          (a.variantId ?? 0) - (b.variantId ?? 0)
      );

  const normApi = normalize(apiCart);
  const normMerged = normalize(mergedCart);

  if (normApi.length !== normMerged.length) return false;

  return normApi.every(
    (item, idx) =>
      item.productId === normMerged[idx].productId &&
      item.variantId === normMerged[idx].variantId &&
      item.quantity === normMerged[idx].quantity
  );
}
export function applySpecialDiscountToCartItem(
  item: CartItem,
  existingCart: CartItem[]
): { updatedItem: CartItem; freeItems: CartItem[] } {
  const freeItems: CartItem[] = [];
  let discountAmount = 0;
  let finalPrice = item.price; // always calculate default

  const discount = item.specialDiscountStatus ? item.specialDiscount : null;

  if (discount && item.quantity >= discount.minimumQuantity) {
    if (discount.discountType === 0) {
      // --- Percentage discount ---
      const discountPerUnit = (item.price * discount.value) / 100;
      discountAmount = discountPerUnit;
      finalPrice = finalPrice - discountAmount;
    } else if (discount.discountType === 1) {
      // --- Free items offer ---
      const freeQty =
        Math.floor(item.quantity / discount.minimumQuantity) * discount.value;

      if (freeQty > 0) {
        const freeId = item.variant
          ? `${item.productId}-${item.variant.id}-free`
          : `${item.productId}-no-variant-free`;

        const existingFreeItem = existingCart.find((ci) => ci.id === freeId);

        if (existingFreeItem) {
          existingFreeItem.quantity = freeQty;
        } else {
          freeItems.push({
            ...item,
            id: freeId,
            quantity: freeQty,
            price: 0,
            discountAmount: 0,
            finalPrice: 0,
            title: item.title + " (Free)",
          });
        }
      }
    }
  }

  // ✅ Always return item with discountAmount + finalPrice keys
  return {
    updatedItem: {
      ...item,
      discountAmount,
      finalPrice,
    },
    freeItems,
  };
}

export function applySpecialDiscountsToCart(cart: CartItem[]): CartItem[] {
  const finalCart: CartItem[] = [];

  cart.forEach((item) => {
    const { updatedItem, freeItems } = applySpecialDiscountToCartItem(
      item,
      finalCart
    );
    finalCart.push(updatedItem);
    finalCart.push(...freeItems);
  });

  return finalCart;
}
